export default function PrivacyPolicy() {
  const updated = "May 13, 2026";

  const s = {
    page: {
      minHeight: "100vh",
      background: "#0a1929",
      color: "#e2e8f0",
      fontFamily: "'Inter', -apple-system, sans-serif",
      fontSize: 15,
      lineHeight: 1.7,
    },
    inner: {
      maxWidth: 760,
      margin: "0 auto",
      padding: "60px 24px 100px",
    },
    wordmark: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.15em",
      textTransform: "uppercase",
      color: "#ef4444",
      marginBottom: 12,
    },
    h1: {
      fontSize: 28,
      fontWeight: 800,
      color: "#f1f5f9",
      margin: "0 0 6px",
      lineHeight: 1.2,
    },
    updated: {
      fontSize: 12,
      color: "#64748b",
      marginBottom: 48,
    },
    h2: {
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#94a3b8",
      margin: "40px 0 12px",
      paddingBottom: 8,
      borderBottom: "1px solid #1e293b",
    },
    p: {
      margin: "0 0 14px",
      color: "#cbd5e1",
    },
    ul: {
      margin: "0 0 14px",
      paddingLeft: 20,
      color: "#cbd5e1",
    },
    li: {
      marginBottom: 6,
    },
    a: {
      color: "#60a5fa",
      textDecoration: "none",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: 16,
      fontSize: 13,
    },
    th: {
      textAlign: "left",
      padding: "8px 12px",
      background: "#0f1f33",
      color: "#94a3b8",
      fontWeight: 600,
      borderBottom: "1px solid #1e293b",
    },
    td: {
      padding: "8px 12px",
      borderBottom: "1px solid #1a2942",
      color: "#cbd5e1",
      verticalAlign: "top",
    },
    divider: {
      border: "none",
      borderTop: "1px solid #1e293b",
      margin: "48px 0 0",
    },
    footer: {
      marginTop: 16,
      fontSize: 12,
      color: "#475569",
    },
  };

  return (
    <div style={s.page}>
      <div style={s.inner}>

        <div style={s.wordmark}>Pollis</div>
        <h1 style={s.h1}>Privacy Policy</h1>
        <p style={s.updated}>Last updated: {updated}</p>

        <p style={s.p}>
          Pollis is a campaign intelligence and social media management platform operated by Pollis.
          This Privacy Policy explains how we collect, use, store, and protect information when you use
          our platform, including when you connect your social media accounts.
        </p>
        <p style={s.p}>
          By using Pollis, you agree to the collection and use of information described in this policy.
        </p>

        <h2 style={s.h2}>1. Who This Policy Covers</h2>
        <p style={s.p}>
          This policy applies to campaign managers, political organizations, and their staff members
          ("Users") who access the Pollis dashboard at{" "}
          <a href="https://pollis.us" style={s.a}>pollis.us</a>. It also covers the social media accounts
          Users connect to the platform for content publishing and engagement tracking.
        </p>

        <h2 style={s.h2}>2. Information We Collect</h2>

        <p style={s.p}><strong style={{ color: "#e2e8f0" }}>Account Information</strong></p>
        <ul style={s.ul}>
          <li style={s.li}>Email address and name (for manager login via magic link)</li>
          <li style={s.li}>Campaign and organization details you provide during onboarding</li>
        </ul>

        <p style={s.p}><strong style={{ color: "#e2e8f0" }}>Connected Social Media Accounts</strong></p>
        <p style={s.p}>
          When you connect a social media account to Pollis, we collect and store:
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Your account username, display name, and platform user ID</li>
          <li style={s.li}>OAuth access tokens and refresh tokens — stored encrypted (AES-256-GCM) and never exposed in plaintext outside our systems</li>
          <li style={s.li}>The OAuth scopes you granted (e.g., permission to read and post)</li>
          <li style={s.li}>Page IDs, channel IDs, and organization IDs associated with your accounts</li>
        </ul>

        <p style={s.p}><strong style={{ color: "#e2e8f0" }}>Content and Activity</strong></p>
        <ul style={s.ul}>
          <li style={s.li}>Posts, captions, and messages you draft and publish through Pollis</li>
          <li style={s.li}>Images and media you upload for publishing</li>
          <li style={s.li}>Direct messages you send through connected accounts via Pollis</li>
          <li style={s.li}>Engagement data retrieved from platforms (impressions, likes, comments, shares, reach)</li>
        </ul>

        <p style={s.p}><strong style={{ color: "#e2e8f0" }}>Analytics Data</strong></p>
        <ul style={s.ul}>
          <li style={s.li}>Website session and traffic data from Google Analytics (if connected)</li>
          <li style={s.li}>Platform-level analytics from connected accounts (e.g., YouTube channel stats)</li>
        </ul>

        <p style={s.p}><strong style={{ color: "#e2e8f0" }}>Campaign Intelligence Data</strong></p>
        <ul style={s.ul}>
          <li style={s.li}>Polling data, fundraising data, and prediction market data you import or that is automatically synced</li>
          <li style={s.li}>FEC filing data retrieved from public government APIs</li>
          <li style={s.li}>Kalshi prediction market data (publicly available market prices)</li>
        </ul>

        <h2 style={s.h2}>3. How We Use Your Information</h2>
        <ul style={s.ul}>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Publishing content</strong> — posting to Facebook Pages, Instagram Business accounts, X (Twitter), TikTok, LinkedIn organization pages, and YouTube using your connected OAuth tokens</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Engagement tracking</strong> — reading post performance metrics (impressions, reach, engagements) from connected platforms to display in your dashboard</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Direct messaging</strong> — sending and reading DMs on Facebook Messenger and Instagram when you use the messaging feature in the dashboard</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Analytics reporting</strong> — surfacing website and channel performance data within your campaign dashboard</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Intelligence generation</strong> — aggregating your campaign data to generate strategic briefings and recommendations using AI language models</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Authentication</strong> — verifying your identity when you log in</li>
        </ul>
        <p style={s.p}>
          We do not use your social media account data for advertising, behavioral profiling, or any
          purpose beyond operating the features you explicitly use.
        </p>

        <h2 style={s.h2}>4. Connected Platforms and Their APIs</h2>
        <p style={s.p}>
          Pollis connects to the following platforms on your behalf. Each platform's data use is
          governed by their respective API terms and privacy policies:
        </p>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Platform</th>
              <th style={s.th}>What We Access</th>
              <th style={s.th}>Platform Privacy Policy</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["X (Twitter)", "Post creation, media upload, DM sending, engagement metrics", "https://twitter.com/en/privacy"],
              ["Facebook", "Page posts, engagement insights, Messenger DMs", "https://www.facebook.com/privacy/policy"],
              ["Instagram", "Business account posts, engagement insights, DMs", "https://privacycenter.instagram.com/policy"],
              ["TikTok", "Video publishing, video performance stats", "https://www.tiktok.com/legal/page/us/privacy-policy"],
              ["LinkedIn", "Organization page posts, post analytics", "https://www.linkedin.com/legal/privacy-policy"],
              ["YouTube", "Video uploads, channel statistics", "https://policies.google.com/privacy"],
              ["Google Analytics", "Website traffic and session data (read-only)", "https://policies.google.com/privacy"],
            ].map(([platform, access, link]) => (
              <tr key={platform}>
                <td style={s.td}><strong style={{ color: "#e2e8f0" }}>{platform}</strong></td>
                <td style={s.td}>{access}</td>
                <td style={s.td}><a href={link} style={s.a} target="_blank" rel="noopener noreferrer">View policy ↗</a></td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 style={s.h2}>5. Data Storage and Security</h2>
        <ul style={s.ul}>
          <li style={s.li}>All data is stored on AWS infrastructure in the United States (us-east-1)</li>
          <li style={s.li}>OAuth access tokens and refresh tokens are encrypted using AES-256-GCM before being written to our database. They are decrypted only in memory at the moment an API call is made</li>
          <li style={s.li}>Our database (Amazon RDS) is isolated in a VPC — it is not publicly accessible</li>
          <li style={s.li}>Campaign data files are stored in private Amazon S3 buckets scoped to each campaign</li>
          <li style={s.li}>Access to campaign data is strictly separated — no campaign can access another campaign's data</li>
          <li style={s.li}>We use HTTPS/TLS for all data in transit</li>
        </ul>

        <h2 style={s.h2}>6. Data Sharing</h2>
        <p style={s.p}>
          We do not sell, rent, or trade your personal information or your social media data to third parties.
        </p>
        <p style={s.p}>We share data only in these limited circumstances:</p>
        <ul style={s.ul}>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>With connected platforms</strong> — when you publish a post or send a DM, your content is transmitted to the relevant platform's API</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>With AI providers</strong> — campaign data (polling, fundraising, engagement) is sent to Anthropic's Claude API to generate strategic briefings. No personally identifiable voter information is included in these requests</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>As required by law</strong> — we will disclose information if required by a valid legal process</li>
        </ul>

        <h2 style={s.h2}>7. Your Rights and Choices</h2>
        <ul style={s.ul}>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Disconnect social accounts</strong> — you can revoke Pollis's access to any connected social media account at any time from your dashboard. This immediately deletes the stored tokens for that platform</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Revoke platform access directly</strong> — you can revoke access through the platform's own settings (e.g., Twitter Connected Apps, Facebook App Permissions)</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Access your data</strong> — you may request a copy of the data we hold about you by contacting us</li>
          <li style={s.li}><strong style={{ color: "#e2e8f0" }}>Delete your data</strong> — you may request deletion of your account and associated data by contacting us</li>
        </ul>

        <h2 style={s.h2}>8. Data Retention</h2>
        <ul style={s.ul}>
          <li style={s.li}>OAuth tokens are retained for as long as a social account is connected, and permanently deleted when you disconnect</li>
          <li style={s.li}>Post history and engagement data are retained for the duration of your account and deleted upon request</li>
          <li style={s.li}>Campaign intelligence outputs (briefs, simulations) are retained for up to 30 versions and then archived or deleted</li>
          <li style={s.li}>Inactive accounts are reviewed after 24 months</li>
        </ul>

        <h2 style={s.h2}>9. Children's Privacy</h2>
        <p style={s.p}>
          Pollis is intended for political campaign professionals and organizations. We do not
          knowingly collect information from individuals under 18 years of age.
        </p>

        <h2 style={s.h2}>10. Changes to This Policy</h2>
        <p style={s.p}>
          We may update this Privacy Policy periodically. When we make material changes, we will update
          the "Last updated" date above and notify active users by email. Continued use of Pollis after
          a policy update constitutes acceptance of the revised terms.
        </p>

        <h2 style={s.h2}>11. Contact Us</h2>
        <p style={s.p}>
          For questions about this Privacy Policy, to request access to your data, or to request deletion:
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Email: <a href="mailto:privacy@pollis.us" style={s.a}>privacy@pollis.us</a></li>
          <li style={s.li}>Website: <a href="https://pollis.us" style={s.a}>pollis.us</a></li>
          <li style={s.li}>Operated by: Pollis</li>
        </ul>

        <hr style={s.divider} />
        <p style={s.footer}>
          © {new Date().getFullYear()} Pollis ·<a href="https://pollis.us" style={{ ...s.a, color: "#475569" }}>pollis.us</a>
        </p>

      </div>
    </div>
  );
}
